import {Box, Occupancy} from "@variocube/app-api";
import React, {Fragment, useCallback, useState} from "react";
import {
  BackButton,
  Badge,
  classNames,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  layout,
  MaterialIcon,
  PushButton,
  Table,
  TableCell,
  TableHead,
  TableRow,
  typography,
  View,
  ViewContent, ViewFooter
} from "@variocube/cube-ui";
import {Stack} from "@mui/material"
import {useLocalization} from "../../i18n";
import {AccessLevel, Controller} from "../controller/Controller";

interface BoxDetailViewProps {
  box: Box;
  occupancies: Occupancy[];
  onOpen: (box: Box) => void;
  canOpen: boolean;
  accessLevel: AccessLevel;
  controller: Controller;
  onBack: () => void;
}

export function BoxDetailView( {box, occupancies, canOpen, onOpen, accessLevel, controller, onBack}: BoxDetailViewProps ) {

  const {t} = useLocalization();

  const [showEnterMaintenanceDialog, setShowEnterMaintenanceDialog] = useState(false);
  const [showLeaveMaintenanceDialog, setShowLeaveMaintenanceDialog] = useState(false);
  const [showEndOccupancyDialog, setShowEndOccupancyDialog] = useState(false);
  const [maintenanceRequiredAt, setMaintenanceRequiredAt] = useState(box.maintenanceRequiredAt);

  const handleOpen = useCallback(() => onOpen(box), [onOpen, box]);

  const handleEnterMaintenance = useCallback(async () => {
    await controller.updateBoxMaintenanceState(box.number, true);
    hideEnterMaintenance();
    setMaintenanceRequiredAt(new Date().toISOString());
  }, [controller, box]);

  const handleLeaveMaintenance = useCallback(async () => {
    await controller.updateBoxMaintenanceState(box.number, false);
    hideLeaveMaintenance();
    setMaintenanceRequiredAt(undefined);
  }, [controller, box]);

  function showEnterMaintenance() {
    setShowEnterMaintenanceDialog(true)
  }

  function hideEnterMaintenance() {
    setShowEnterMaintenanceDialog(false)
  }
  function showLeaveMaintenance() {
    setShowLeaveMaintenanceDialog(true)
  }

  function hideLeaveMaintenance() {
    setShowLeaveMaintenanceDialog(false)
  }

  function showEndOccupancy() {
    setShowEndOccupancyDialog(true);
  }

  function hideEndOccupancy() {
    setShowEndOccupancyDialog(false);
  }

 async function handleEndOccupancy(){
    if(occupancies){
      try{
        for(const o of occupancies )
        await controller.endOccupancy(o.uuid);
      }catch(e){
        console.error("Failed to end occupancy", e);
      }
    }
    hideEndOccupancy();
  }

  const handleBack = () => {
    onBack();
  }

  return(
    <Fragment>
      <View>
      <Stack direction="row">
        <BackButton label={t("Back")} onClick={handleBack} />
        <h2 className={classNames(typography.title, typography.colorSuccess, layout.paddingSmall)}>{t("Box")} {box.number}</h2>
      </Stack>
      <h2 className={classNames(typography.title, layout.paddingSmall, layout.marginYSmall)}>{t("BoxDetailAllOccupancies")}</h2>
      <ViewContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>App ID</TableCell>
              <TableCell>Access Code</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
            {(occupancies.length > 0) &&
                occupancies.map( (o) => (
                  <BoxDetailRow
                    key={o.uuid}
                    occupancy={o}
                  />
                ))
            }
            {(occupancies.length == 0) &&
              <TableRow>
                  <>
                  <View className={layout.paddingMedium}>
                    <div>{t("BoxDetailNoOccupancies")}</div>
                  </View>
                  </>
              </TableRow>
            }
        </Table>
      </ViewContent>
      <ViewFooter className={classNames(layout.horizontal, layout.paddingMedium, layout.spaceEvenly)}>
          {canOpen && (
            <div>
              <PushButton
                className={layout.paddingSmall}
                icon={MaterialIcon.lock_open}
                primary
                onClick={handleOpen}
              >
                {t("Open")}
              </PushButton>
            </div>
          )}
          <div>
            {!maintenanceRequiredAt &&
              <PushButton
                icon={MaterialIcon.maintenance}
                danger
                onClick={showEnterMaintenance}
              >
                {t("MaintenanceEnter")}
              </PushButton>
            }
            {maintenanceRequiredAt &&
              <PushButton
                icon={MaterialIcon.maintenance}
                secondary
                onClick={showLeaveMaintenance}
              >
                {t("MaintenanceLeave")}
              </PushButton>
            }
            <Dialog open={showEnterMaintenanceDialog} onBackdropClick={hideEnterMaintenance}>
              <DialogHeader title={t('MaintenanceEnter')} />
              <DialogContent>
                <div>{t('MaintenanceEnterPromptLeading')}</div>
                <div>{t('MaintenanceEnterPromptTrailing')}</div>
              </DialogContent>
              <DialogFooter>
                <PushButton danger onClick={handleEnterMaintenance}>{t('Confirm')}</PushButton>
                &nbsp;
                <PushButton secondary onClick={hideEnterMaintenance}>{t('Cancel')}</PushButton>
              </DialogFooter>
            </Dialog>
            <Dialog open={showLeaveMaintenanceDialog} onBackdropClick={hideLeaveMaintenance}>
              <DialogHeader title={t('MaintenanceLeave')} />
              <DialogContent>
                <div>{t('MaintenanceLeavePromptLeading', { maintenanceRequiredAt })}</div>
                <div>{t('MaintenanceLeavePromptTrailing')}</div>
              </DialogContent>
              <DialogFooter>
                <PushButton success onClick={handleLeaveMaintenance}>{t('Confirm')}</PushButton>
                &nbsp;
                <PushButton secondary onClick={hideLeaveMaintenance}>{t('Cancel')}</PushButton>
              </DialogFooter>
            </Dialog>
          </div>
          {(occupancies.length > 0) && (accessLevel == AccessLevel.FullAccess) &&
            <div>
              <PushButton
                  icon={MaterialIcon.maintenance}
                  danger
                  onClick={showEndOccupancy}
              >
                {t("BoxDetailEndOccupancy")}
              </PushButton>
              <Dialog open={showEndOccupancyDialog} onBackdropClick={hideEndOccupancy}>
                  <DialogHeader title={t("BoxDetailEndOccupancy")} />
                  <DialogContent>
                      <div>{t("BoxDetailReallyEndOccupancy")}</div>
                  </DialogContent>
                  <DialogFooter>
                    <PushButton danger onClick={handleEndOccupancy}>{t('Confirm')}</PushButton>
                      &nbsp;
                    <PushButton secondary onClick={hideEndOccupancy}>{t('Cancel')}</PushButton>
                  </DialogFooter>
              </Dialog>
            </div>
          }
      </ViewFooter>
      </View>
    </Fragment>
  );
}

interface BoxDetailRowProps {
  occupancy: Occupancy;
}

function BoxDetailRow({occupancy}: BoxDetailRowProps) {
  return(
    <TableRow>
      <TableCell><Badge>{occupancy.appId}</Badge></TableCell>
      <TableCell>{occupancy.accessCode}</TableCell>
      <TableCell>{occupancy.created}</TableCell>
  </TableRow>
  );
}